import logger from '../../utils/logger';
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, ArrowLeft } from 'lucide-react';
import '../../styles/auth.css';

/**
 * MFA Verification Component
 *
 * Used during login to verify the TOTP code from authenticator app
 * - 6-digit code input with auto-focus
 * - Option to use recovery code
 * - "Trust this device" checkbox for 30-day bypass
 *
 * Updated to match latest app design (light theme with grid pattern)
 */
const MfaVerification = ({ onVerify, onCancel, onUseRecoveryCode, pendingLoginData }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  const inputRefs = useRef([]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Handle individual digit input
  const handleDigitChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      handleVerify(fullCode);
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();

      // Auto-submit
      handleVerify(pastedData);
    }
  };

  // Verify TOTP code
  const handleVerify = async (codeToVerify) => {
    const verificationCode = codeToVerify || code.join('');

    if (verificationCode.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    if (!pendingLoginData || !pendingLoginData.email || !pendingLoginData.password) {
      setError('Session expired. Please login again.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Backend requires email/password + MFA code
      const response = await fetch(`/api/auth/mfa/complete?mfaCode=${encodeURIComponent(verificationCode)}&isRecoveryCode=false`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: pendingLoginData.email,
          password: pendingLoginData.password
        })
      });

      const data = await response.json();

      if (data.success) {
        if (onVerify) {
          onVerify(data);
        }
      } else {
        setError(data.message || 'Invalid verification code');
        // Clear code on error
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err.message || 'Failed to verify code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Verify recovery code
  const handleRecoveryCodeVerify = async () => {
    if (!recoveryCode || recoveryCode.length < 8) {
      setError('Please enter a valid recovery code');
      return;
    }

    if (!pendingLoginData || !pendingLoginData.email || !pendingLoginData.password) {
      setError('Session expired. Please login again.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const cleanedRecoveryCode = recoveryCode.toUpperCase().replace(/\s/g, '');

      // Backend requires email/password + recovery code
      const response = await fetch(`/api/auth/mfa/complete?mfaCode=${encodeURIComponent(cleanedRecoveryCode)}&isRecoveryCode=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: pendingLoginData.email,
          password: pendingLoginData.password
        })
      });

      const data = await response.json();

      if (data.success) {
        if (onUseRecoveryCode) {
          onUseRecoveryCode(data);
        }
      } else {
        setError(data.message || 'Invalid recovery code');
        setRecoveryCode('');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify recovery code');
      setRecoveryCode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-4" style={{ background: '#000000', color: '#ffffff' }}>
      {/* Grid Pattern Background */}
      <div className="grid-bg-dark" />
      <div className="glow-gradient-dark" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl p-8 max-w-md w-full relative"
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-center mb-6">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <Shield className="w-8 h-8" style={{ color: '#10b981' }} />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white text-center mb-2">
          Two-Factor Authentication
        </h2>

        <p className="text-center mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          {showRecoveryInput
            ? 'Enter one of your recovery codes'
            : 'Enter the code from your authenticator app'}
        </p>

        {!showRecoveryInput ? (
          <>
            {/* 6-digit code input */}
            <div className="flex gap-2 mb-6 justify-center">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-white text-2xl font-mono text-center rounded-xl focus:outline-none transition-colors"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  disabled={isLoading}
                />
              ))}
            </div>

            {/* Trust device checkbox */}
            <label className="flex items-center gap-2 mb-6 cursor-pointer group">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-transparent text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
              />
              <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Trust this device for 30 days
              </span>
            </label>
          </>
        ) : (
          <>
            {/* Recovery code input */}
            <div className="mb-6">
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => {
                  setRecoveryCode(e.target.value.toUpperCase());
                  setError('');
                }}
                placeholder="XXXX-XXXX-XXXX"
                className="w-full text-white px-4 py-3 rounded-xl text-center text-lg font-mono tracking-wider focus:outline-none placeholder:text-gray-500"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
                autoFocus
                disabled={isLoading}
              />
            </div>

            <button
              onClick={handleRecoveryCodeVerify}
              disabled={!recoveryCode || isLoading}
              className="btn-primary-dark w-full mb-4 py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verifying...' : 'Verify Recovery Code'}
            </button>

            <button
              onClick={() => {
                setShowRecoveryInput(false);
                setRecoveryCode('');
                setError('');
              }}
              className="btn-secondary-dark w-full py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Authenticator Code
            </button>
          </>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-800/30 rounded-xl flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Actions */}
        {!showRecoveryInput && (
          <div className="space-y-3">
            <button
              onClick={() => setShowRecoveryInput(true)}
              className="w-full text-sm font-medium transition-colors"
              style={{ color: '#10b981' }}
              onMouseEnter={(e) => e.target.style.color = '#34d399'}
              onMouseLeave={(e) => e.target.style.color = '#10b981'}
            >
              Use recovery code instead
            </button>

            {onCancel && (
              <button
                onClick={onCancel}
                className="w-full text-sm transition-colors"
                style={{ color: 'rgba(255, 255, 255, 0.4)' }}
                onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.7)'}
                onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.4)'}
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {/* Help text */}
        <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <p className="text-xs text-center" style={{ color: 'rgba(255, 255, 255, 0.35)' }}>
            {showRecoveryInput
              ? 'Recovery codes are one-time use only. You received these when you set up 2FA.'
              : 'Open your authenticator app and enter the 6-digit code to continue.'}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default MfaVerification;
